/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './ad-tag-generator-page.component.html';
import {Uuid} from "../../components/util/uuid";
import {UrlParamsEncoder} from "../../components/util/encode-as-url-params";
import {AdServerConfig} from "../../components/api/api";
import {Session} from "../../components/session/session";
import {AdTagReplacers} from "../ad-unit/ad-tag-replacers";
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {SearchResource} from '../../components/api/resources/resource/search';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';

type AdUnitSearchResult = {
  adUnitTag?: string;
  name?: string;
  width?: string | number;
  height?: string | number;
};

type AdTagModel = {
  auId: string;
  auW?: string | number;
  auH?: string | number;
  targetId?: string;
  container: string;
  load: string;
  proximity: number;
  dimensions?: Array<[number | undefined, number | undefined]>;
  kvs?: Array<{k: string; v: string}>;
  cs?: string;
  ks?: string;
  auml?: string;
  searchAu?: AdUnitSearchResult;
  vast2?: string;
  vast3?: string;
  vast4?: string;
  vast4_1?: string;
  vast4_2?: string;
  native?: string;
};

@Component({
  standalone: false,
  selector: 'adn-ad-tag-generator-page',
  template
})
export class AdTagGeneratorPageComponent implements OnInit {
  readonly codeMirrorModel: any = this;
  activeExampleTab: 'adTag' | 'emailTag' | 'vast' | 'nativeAds' | 'htmlDocument' | 'requestAds' | 'adTagLinks' = 'adTag';
  readonly containerOptions = [
    {id: 'iframe', text: 'iFrame (default)'},
    {id: 'div', text: 'div tag (ad is rendered directly into page)'},
    {id: 'isolate', text: 'isolate (ad in isolated iFrame, blocking third-party cookies, potential problems with some ads)'}
  ];
  readonly adUnitContainerOptions = [
    {id: '', text: 'inherit global setting'},
    ...this.containerOptions
  ];
  readonly loadOptions = [
    {id: 'direct', text: 'Direct (ad request and rendering done as soon as possible)'},
    {id: 'lazy', text: 'Lazy Load (ad requested immediately, rendering conditional)'},
    {id: 'lazyRequest', text: 'Lazy Request (ad request is conditional, rendering immediate)'}
  ];
  readonly adUnitLoadOptions = [
    {id: '', text: 'inherit global setting'},
    ...this.loadOptions
  ];
  readonly cookieOptions = [
    {id: 'allowAll', text: 'Allow Cookies and Local Storage'},
    {id: 'blockAll', text: 'No Cookies and no Local Storage'},
    {id: 'blockCookies', text: 'No Cookies and use Local Storage'}
  ];

  readonly defaultMailchimp = '*|CAMPAIGN_UID|**|UNIQID|*';

  readonly ogHtml = '<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="utf-8">\n<title>Adnuntius Ad Tag Generator and Tester</title>\n</head>\n<body><h3>Adnuntius Ad Tag Generator and Tester</h3>\n<!--DIVS-->\n<!--JS-->\n</body>\n</html>';
  readonly ogJs = "\x3Cscript src=\"https://cdn.adnuntius.com/adn.js\" async>\x3C/script>\n\x3Cscript>\nwindow.adn = window.adn || {}; adn.calls = adn.calls || [];\nadn.calls.push(function() {/*PREREQUEST*/\n   adn.request({ /*OUTSIDE*/ adUnits: [\n      /*ADUNITS*/\n   ]});\n});\n\x3C/script>";
  readonly ogEmail = "<a href=\"https://delivery.adnuntius.com/c?network=[[TAG_ID]]&auId=[[AD_UNIT]]&autor=true&iid=[THIS_MUST_BE_REPLACED]\">\n    <img src=\"https://delivery.adnuntius.com/i?[[GDPR]]network=[[TAG_ID]]&auId=[[AD_UNIT]][[TARGETING]]&format=image&iid=[THIS_MUST_BE_REPLACED]\"[[AD_UNIT_WIDTH]][[AD_UNIT_HEIGHT]] />\n</a>\n";
  readonly ogVast = "https://delivery.adnuntius.com/i?[[GDPR]]network=[[TAG_ID]]&auId=[[AD_UNIT]][[TARGETING]]&tt=[[VAST_VERSION]]";
  readonly ogNative = "https://delivery.adnuntius.com/i?[[GDPR]]network=[[TAG_ID]]&auId=[[AD_UNIT]][[TARGETING]]&tt=native";

  @ViewChild('previewContainer') previewContainer?: ElementRef<HTMLDivElement>;

  adnCode = '';
  emailCode = '';
  htmlCode = '';
  publicLink = '';
  privateLink = '';
  adUnitResults: any[] = [];
  adTags: AdTagModel[] = [];

  hideGlobal = false;
  canSearch = false;
  whitelabelDomain = '';
  whitelabel = false;

  idType = 'CUSTOM';
  uniqueId?: string;
  gdpr?: number;
  segments?: string;

  container = 'iframe';
  load = 'direct';
  cookies = 'allowAll';
  proximity = 0;

  private readonly networkTagId: string;
  private readonly defaultAdTag: AdTagModel;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(SearchResource) private readonly searchResource: any,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly uiRouter: UIRouter
  ) {
    this.networkTagId = this.LocalNetworkProfile.get('tagId');
    this.defaultAdTag = this.buildDefaultAdTag();
  }

  ngOnInit(): void {
    this.canSearch = this.isAuthenticated();
    this.whitelabelDomain = AdTagReplacers.getNetworkWhitelabelDomain(this.LocalNetworkProfile.getNetworkTag());
    this.whitelabel = !!this.whitelabelDomain;

    const designAdTag = this.readDesignAdTags();
    if (designAdTag) {
      this.adTags = _.cloneDeep(designAdTag);
    } else {
      this.addAdUnit();
    }

    this.defaultAdTag.auW = undefined;
    this.defaultAdTag.auH = undefined;
    this.defaultAdTag.auId = '0000000000000001';

    this.updateGeneratedCode();
  }

  searchAdUnits = (searchElement: any): void => {
    this.searchResource.query({q: searchElement.search, types: 'AdUnit'}).then((data: any) => {
      this.adUnitResults = data.searchResults;
    });
  };

  setGdpr(value?: number): void {
    this.gdpr = value;
    this.changeValue();
  }

  useSearchResults(adTag: AdTagModel): void {
    if (!adTag.searchAu) {
      return;
    }
    adTag.auId = adTag.searchAu.adUnitTag || adTag.auId;
    adTag.auW = adTag.searchAu.width;
    adTag.auH = adTag.searchAu.height;
    this.changeValue();
  }

  addAdUnit(): void {
    this.adTags.push(_.cloneDeep(this.defaultAdTag));
    delete this.defaultAdTag.searchAu;
    this.updateGeneratedCode();
  }

  changeValue(): void {
    this.updateGeneratedCode();
  }

  removeAdUnit(index: number): void {
    _.pullAt(this.adTags, index);
    this.updateGeneratedCode();
  }

  addSegments(): void {
    this.segments = '';
  }

  removeSegments(): void {
    this.segments = undefined;
    this.updateGeneratedCode();
  }

  addDimensions(adTag: AdTagModel): void {
    adTag.dimensions = adTag.dimensions || [];
    adTag.dimensions.push([undefined, undefined]);
  }

  removeDimensions(adTag: AdTagModel, index: number): void {
    _.pullAt(adTag.dimensions || [], index);
    this.updateGeneratedCode();
  }

  addKv(adTag: AdTagModel): void {
    adTag.kvs = adTag.kvs || [];
    adTag.kvs.push({k: '', v: ''});
  }

  addC(adTag: AdTagModel): void {
    adTag.cs = '';
  }

  addK(adTag: AdTagModel): void {
    adTag.ks = '';
  }

  removeCs(adTag: AdTagModel): void {
    delete adTag.cs;
    this.updateGeneratedCode();
  }

  removeKs(adTag: AdTagModel): void {
    delete adTag.ks;
    this.updateGeneratedCode();
  }

  addAuml(adTag: AdTagModel): void {
    adTag.auml = '';
  }

  removeAumls(adTag: AdTagModel): void {
    delete adTag.auml;
    this.updateGeneratedCode();
  }

  removeKv(adTag: AdTagModel, index: number): void {
    _.pullAt(adTag.kvs || [], index);
    this.updateGeneratedCode();
  }

  doPreview(): void {
    if (!this.previewContainer) {
      return;
    }

    const previewElement = this.previewContainer.nativeElement;
    const existingIframe = previewElement.querySelector('iframe');
    if (existingIframe) {
      previewElement.removeChild(existingIframe);
    }

    const ifr = this.document.createElement('iframe');
    ifr.setAttribute('style', 'width: 100%; min-height: 500px; border: 2px solid #ddd; padding: 5px;');
    ifr.setAttribute('width', '100%');
    ifr.setAttribute('scrolling', 'yes');
    ifr.setAttribute('frameBorder', '0');
    previewElement.appendChild(ifr);

    const iframeDoc = ifr.contentDocument || ifr.contentWindow?.document;
    if (!iframeDoc) {
      return;
    }

    iframeDoc.open();
    iframeDoc.write(this.htmlCode);
    iframeDoc.close();
    if (iframeDoc.body) {
      iframeDoc.body.setAttribute('style', 'margin: 0; padding: 0;');
    }
  }

  private buildDefaultAdTag(): AdTagModel {
    const routeParams = this.uiRouter.globals.params;
    const defaultAdTag: AdTagModel = {
      auId: routeParams.auId || '0000000000000001',
      auW: routeParams.auW || undefined,
      auH: routeParams.auH || undefined,
      container: '',
      load: '',
      proximity: 0
    };

    if (routeParams.auName) {
      defaultAdTag.searchAu = {
        adUnitTag: routeParams.auId,
        name: routeParams.auName
      };
    }

    return defaultAdTag;
  }

  private readDesignAdTags(): AdTagModel[] | null {
    const design = this.uiRouter.globals.params.design;
    if (!design) {
      return null;
    }

    try {
      return JSON.parse(design);
    } catch (_error) {
      return null;
    }
  }

  private isAuthenticated(): boolean {
    const session: any = Session.obtainData();
    return !!session && !_.isEmpty(session) && session.scope === 'NG_API';
  }

  getCodeMirrorFieldName(base: string, index?: number): string {
    return index === undefined ? base : `${base}-${index}`;
  }

  selectExampleTab(tab: 'adTag' | 'emailTag' | 'vast' | 'nativeAds' | 'htmlDocument' | 'requestAds' | 'adTagLinks'): void {
    this.activeExampleTab = tab;
  }

  private splitStringsReplacer(splitStringArray: string[], justTheValue?: boolean): string[] {
    return _.map(_.filter(_.map(splitStringArray, function(v) {
      return _.trim(v);
    }), function(v) {
      return _.isString(v) && v.length > 0;
    }), function(v) {
      return justTheValue ? v : "'" + v + "'";
    });
  }

  private valueReplacement(commaData: string, justTheValue?: boolean): string | string[] {
    const splitStrings = commaData.split(',');
    const mappedValues = this.splitStringsReplacer(splitStrings, justTheValue);
    return justTheValue ? mappedValues : "[" + mappedValues.join(', ') + "]";
  }

  private auTagCommentGen(adTag: AdTagModel): {target: string; comment: string} {
    const theTarget = adTag.targetId ? adTag.targetId : "adn-" + adTag.auId;
    const adTagName = _.get(adTag, ['searchAu', 'name']);
    const commentTagContents = adTagName ? adTagName + " (" + adTag.auId + ")" : adTag.auId;

    return {
      target: theTarget,
      comment: commentTagContents
    };
  }

  private collectKvs(adTag: AdTagModel, func?: (collector: any[], k: string, v: string) => void): any[] {
    const kvObj: any[] = [];
    _.forEach(adTag.kvs, (entry) => {
      if (_.isString(entry.k) && entry.k.length > 0 && _.isString(entry.v) && entry.v.length > 0) {
        if (_.isFunction(func)) {
          func(kvObj, entry.k, entry.v);
        } else {
          const values = this.valueReplacement(entry.v);
          kvObj.push("'" + entry.k + "': " + values);
        }
      }
    });
    return kvObj;
  }

  private collectDimensions(adTag: AdTagModel): string[] {
    const dims: string[] = [];
    _.forEach(adTag.dimensions, function(entry) {
      if (_.isFinite(entry[0]) && _.isFinite(entry[1])) {
        dims.push("[" + entry[0] + "," + entry[1] + "]");
      }
    });
    return dims;
  }

  private updateGeneratedCode(): void {
    const adUnitStrings: string[] = [];
    const divsReplacement: string[] = [];
    const simpleDivsReplacement: string[] = [];
    const replacer: Record<string, string> = {
      auId: "auId: '%auId%'",
      auW: "auW: '%auW%'",
      auH: "auH: '%auH%'",
      targetId: "targetId: '%targetId%'",
      container: "container: '%container%'",
      requestParams: "requestParams: { load: '%load%', proximity: %proximity% }",
      kvs: 'kv: %kvs%',
      dimensions: 'dimensions: %dimensions%',
      cs: 'c: %cs%',
      ks: 'keywords: %ks%',
      auml: 'auml: %auml%'
    };

    _.forEach(this.adTags, (tag, index) => {
      _.forEach(this.adTags, (otherTag, subIndex) => {
        if (index !== subIndex && otherTag.auId === tag.auId && otherTag.targetId === tag.targetId) {
          otherTag.targetId = "adn-id-" + Uuid.generate();
        }
      });
    });

    _.forEach(this.adTags, (adTag) => {
      const adUnits: string[] = [];
      _.forEach(replacer, (value, key) => {
        const adTagValue = _.get(adTag, key);

        if (!adTagValue && key !== 'requestParams') {
          return;
        }

        if (key === 'cs' || key === 'auml' || key === 'ks') {
          const cReplacement = this.valueReplacement(adTagValue);
          adUnits.push(value.replace('%' + key + '%', cReplacement as string));
          return;
        }

        if (key === 'dimensions') {
          const dimsObj = this.collectDimensions(adTag);
          if (dimsObj.length > 0) {
            adUnits.push(value.replace('%' + key + '%', "[" + dimsObj.join(", ") + "]"));
          }
          return;
        }

        if (key === 'kvs') {
          const kvObj = this.collectKvs(adTag);
          if (kvObj.length > 0) {
            adUnits.push(value.replace('%' + key + '%', "{ " + kvObj.join(", ") + " }"));
          }
          return;
        }

        if (key === 'requestParams') {
          if (adTag.load === 'direct') {
            adUnits.push("requestParams: { load: 'direct' }");
          } else if (adTag.load && _.isFinite(adTag.proximity)) {
            adUnits.push(value.replace('%load%', adTag.load).replace('%proximity%', String(adTag.proximity)));
          }
          return;
        }

        if (key === 'container') {
          if (adTag.container === 'isolate') {
            adUnits.push('isolateFrame: true');
            if (this.container === 'div') {
              adUnits.push("container: 'iframe'");
            }
          } else {
            adUnits.push(value.replace('%' + key + '%', adTag.container));
            if (this.container === 'isolate') {
              adUnits.push('isolateFrame: false');
            }
          }
          return;
        }

        adUnits.push(value.replace('%' + key + '%', String(adTagValue)));
      });

      adUnitStrings.push("{ " + adUnits.join(", ") + " }");
      if (adTag.auId) {
        const tagData = this.auTagCommentGen(adTag);
        simpleDivsReplacement.push("<!-- " + tagData.comment + " -->\n<div id=\"" + tagData.target + "\" style=\"display: none\"></div>");
        divsReplacement.push("<hr>\n<p><small>Ad request: auId = " + adTag.auId + (adTag.targetId ? " and targetId = " + adTag.targetId : "") + "</small></p>\n<div id=\"" + tagData.target + "\" style=\"display: none\"></div>");
      }
    });

    const outsideStrings: string[] = [];
    if (AdServerConfig.obtain().getEnv() !== 'production') {
      outsideStrings.push("env: '" + AdServerConfig.obtain().getEnv() + "'");
    }
    if (this.cookies === 'blockAll' || this.cookies === 'blockCookies') {
      outsideStrings.push('useCookies: false');
    }
    if (this.gdpr === 0 || this.gdpr === 1) {
      outsideStrings.push('gdpr: ' + this.gdpr);
    }
    if (this.container === 'div') {
      outsideStrings.push("container: 'div'");
    }
    if (this.container === 'isolate') {
      outsideStrings.push('isolateFrame: true');
    }
    if (this.load !== 'direct' && _.isFinite(this.proximity)) {
      outsideStrings.push("requestParams: { load: '" + this.load + "', proximity: " + this.proximity + " }");
    }
    if (this.segments) {
      const segmentValues = this.valueReplacement(this.segments);
      outsideStrings.push("segments: " + segmentValues);
    }

    let prerequestReplacement = '';
    if (this.cookies === 'blockAll') {
      prerequestReplacement = 'adn.useLocalStorage(false);';
    }
    prerequestReplacement = prerequestReplacement ? "\n   " + prerequestReplacement : prerequestReplacement;

    const outsideReplacement = outsideStrings.length > 0 ? outsideStrings.join(", ") + "," : '';
    const basicAdCode = this.ogJs
      .replace('/*ADUNITS*/', adUnitStrings.join(",\n      "))
      .replace('/*OUTSIDE*/', outsideReplacement)
      .replace('/*PREREQUEST*/', prerequestReplacement);

    this.adnCode = simpleDivsReplacement.join("\n") + "\n" + basicAdCode;
    this.htmlCode = this.ogHtml.replace('<!--JS-->', basicAdCode).replace('<!--DIVS-->', divsReplacement.join("\n"));

    this.emailCode = '';
    _.forEach(this.adTags, (adTag) => {
      let adUnitString = this.ogEmail
        .replace(/\[\[TAG_ID]]/g, this.networkTagId)
        .replace(/\[\[AD_UNIT]]/g, adTag.auId);
      const widthReplacer = adTag.auW ? " width=\"" + adTag.auW + "\"" : '';
      const heightReplacer = adTag.auH ? " height=\"" + adTag.auH + "\"" : '';
      adUnitString = adUnitString
        .replace('[[AD_UNIT_WIDTH]]', widthReplacer)
        .replace('[[AD_UNIT_HEIGHT]]', heightReplacer);

      const gdprReplacement = this.gdpr === 0 ? 'gdpr=0&' : this.gdpr === 1 ? 'gdpr=1&' : '';
      adUnitString = adUnitString.replace('[[GDPR]]', gdprReplacement);

      if (this.idType === 'MAILCHIMP') {
        adUnitString = adUnitString.replace(/\[THIS_MUST_BE_REPLACED]/g, this.defaultMailchimp);
      } else if (this.uniqueId) {
        adUnitString = adUnitString.replace(/\[THIS_MUST_BE_REPLACED]/g, this.uniqueId);
      }

      const kvObj = this.collectKvs(adTag, (collector, k, v) => {
        const theObj: Record<string, string | string[]> = {};
        theObj[k] = this.valueReplacement(v, true);
        collector.push(theObj);
      });

      const params: Record<string, any> = {};
      if (kvObj.length > 0) {
        params.kv = JSON.stringify(kvObj);
      }
      if (adTag.cs) {
        params.c = this.valueReplacement(adTag.cs, true);
      }
      if (adTag.auml) {
        params.auml = this.valueReplacement(adTag.auml, true);
      }

      const targetingReplacements = _.isEmpty(params) ? '' : UrlParamsEncoder.encode(params, true);
      adUnitString = adUnitString.replace('[[TARGETING]]', targetingReplacements);

      const tagData = this.auTagCommentGen(adTag);
      this.emailCode = this.emailCode + ("<!-- " + tagData.comment + " -->") + "\n" + adUnitString;

      const workingVast = this.ogVast.replace('[[GDPR]]', gdprReplacement);
      adTag.vast2 = workingVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, adTag.auId).replace(/\[\[VAST_VERSION]]/g, 'vast2').replace('[[TARGETING]]', targetingReplacements);
      adTag.vast3 = workingVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, adTag.auId).replace(/\[\[VAST_VERSION]]/g, 'vast3').replace('[[TARGETING]]', targetingReplacements);
      adTag.vast4 = workingVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, adTag.auId).replace(/\[\[VAST_VERSION]]/g, 'vast4').replace('[[TARGETING]]', targetingReplacements);
      adTag.vast4_1 = workingVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, adTag.auId).replace(/\[\[VAST_VERSION]]/g, 'vast4-1').replace('[[TARGETING]]', targetingReplacements);
      adTag.vast4_2 = workingVast.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, adTag.auId).replace(/\[\[VAST_VERSION]]/g, 'vast4-2').replace('[[TARGETING]]', targetingReplacements);

      const workingNative = this.ogNative.replace('[[GDPR]]', gdprReplacement);
      adTag.native = workingNative.replace(/\[\[TAG_ID]]/g, this.networkTagId).replace(/\[\[AD_UNIT]]/g, adTag.auId).replace('[[TARGETING]]', targetingReplacements);

      if (this.whitelabel && this.whitelabelDomain) {
        adTag.vast2 = AdTagReplacers.replace(adTag.vast2, this.whitelabelDomain, this.networkTagId);
        adTag.vast3 = AdTagReplacers.replace(adTag.vast3, this.whitelabelDomain, this.networkTagId);
        adTag.vast4 = AdTagReplacers.replace(adTag.vast4, this.whitelabelDomain, this.networkTagId);
        adTag.vast4_1 = AdTagReplacers.replace(adTag.vast4_1, this.whitelabelDomain, this.networkTagId);
        adTag.vast4_2 = AdTagReplacers.replace(adTag.vast4_2, this.whitelabelDomain, this.networkTagId);
        adTag.native = AdTagReplacers.replace(adTag.native, this.whitelabelDomain, this.networkTagId);
      }
    });

    if (this.whitelabel && this.whitelabelDomain) {
      this.adnCode = AdTagReplacers.replace(this.adnCode, this.whitelabelDomain);
      this.htmlCode = AdTagReplacers.replace(this.htmlCode, this.whitelabelDomain);
      this.emailCode = AdTagReplacers.replace(this.emailCode, this.whitelabelDomain);
    }

    this.adnCode = AdTagReplacers.addNetworkTagId(this.adnCode, this.networkTagId);
    this.htmlCode = AdTagReplacers.addNetworkTagId(this.htmlCode, this.networkTagId);

    const cutDownAdTags = _.map(this.adTags, function(adTag) {
      const otherAdTag = _.cloneDeep(adTag);
      delete otherAdTag.searchAu;
      return otherAdTag;
    });
    const url = this.location.protocol + '://' + this.location.hostname + (this.location.hostname.indexOf('localhost') > -1 ? ':' + this.location.port : '');
    const value = this.window.encodeURIComponent(JSON.stringify(cutDownAdTags));
    this.publicLink = url + '/tag?design=' + value;
    this.privateLink = url + '/ad-tag?design=' + value;
  }
}
